import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import BottomNav from './BottomNav';
import PageHeader from './PageHeader';
import IsometricGarden from './IsometricGarden';
import { createInitialGarden } from './experimental/isometricGardenModel';
import { Icon } from '@iconify/react';
import { HeaderProvider, useHeader } from '../contexts/HeaderContext';
import { useUser } from '../contexts/UserContext';
import { ThemeProvider, useTheme } from '../contexts/ThemeContext';

const HeaderContent = () => {
  const { headerProps } = useHeader();
  const { title, subtitle, rightElement, onNotificationClick } = headerProps;
  // Always pull user from UserContext so pages don't need to pass it manually
  const { user: contextUser } = useUser();
  const contextUserId = contextUser?.user_id || contextUser?.id;

  if (!title) return null; // Don't render header if no title

  return (
    <div className="fixed top-0 left-0 right-0 z-40 pt-safe" style={{ background: 'var(--c-header-bg)' }}>
      <div className="max-w-md mx-auto px-2">
        <PageHeader
          title={title}
          subtitle={subtitle}
          rightElement={rightElement}
          user={contextUser}
          userId={contextUserId}
          onNotificationClick={onNotificationClick}
        />
      </div>
    </div>
  );
};

// Inline variant – used inside the flex layout so it doesn't need position:fixed
const HeaderContentInline = ({ textColorOverride, borderlessOverride }) => {
  const { headerProps } = useHeader();
  const { title, subtitle, rightElement, onNotificationClick } = headerProps;
  const { user: contextUser } = useUser();
  const contextUserId = contextUser?.user_id || contextUser?.id;

  if (!title) return null;

  return (
    <div>
      <div className="max-w-md md:max-w-xl mx-auto px-2">
        <PageHeader
          title={title}
          subtitle={subtitle}
          rightElement={rightElement}
          user={contextUser}
          userId={contextUserId}
          onNotificationClick={onNotificationClick}
          textColor={textColorOverride}
          borderless={borderlessOverride}
        />
      </div>
    </div>
  );
};

const HeaderInlineWrapper = ({ variant }) => {
  return (
    <div
      className="flex-shrink-0 z-40 pt-safe"
      style={{ background: 'var(--c-header-bg)' }}
    >
      <HeaderContentInline textColorOverride={'var(--c-texPri)'} borderlessOverride={variant !== 'plain'} />
    </div>
  );
};

const GardenSubHeader = ({ gardenGrid, completionAnim, forestPulse }) => {
  const grownTiles = gardenGrid.flat().filter(t => t !== 'grass').length;

  return (
    <div className={`flex-shrink-0${forestPulse ? ' forest-pulse' : ''}`} style={{ background: 'linear-gradient(to bottom, var(--c-garden-top) 0%, var(--c-garden-mid1) 20%, var(--c-garden-mid2) 50%, var(--c-garden-mid3) 80%, var(--c-garden-bot) 100%)', position: 'relative', overflow: 'hidden' }}>
      <div style={{ maxWidth: 'clamp(390px, 70vw, 560px)', margin: '0 auto' }}>
        <IsometricGarden grid={gardenGrid} completionAnimation={completionAnim} fullView />
        <div style={{
          position: 'absolute', top: 12, right: 12,
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Icon icon="solar:leaf-bold" style={{ width: 14, height: 14, color: '#4a7c59' }} />
            <span style={{ fontFamily: 'Manrope-Variable', fontSize: 12, fontWeight: 700, color: 'var(--c-texPri)' }}>
              {grownTiles}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const PAGE_ORDER = { '/dashboard': 0, '/plan': 1, '/achievements': 2, '/stats': 3 };

const AuthenticatedLayoutInner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isPremium, loading: userLoading, fetchUserData } = useUser();
  const userId = user?.user_id || user?.id;

  // ── Subscription guard with post-purchase grace window ──
  //
  // Background: after a successful IAP purchase the backend writes
  // `subscription_status=premium` via /subscription/verify-iap, then the
  // frontend (OnboardingV2 polling OR the IAP success handler) calls
  // fetchUserData() and navigates to /dashboard. Several races can leave
  // a stale "not premium" snapshot in this layout for up to a few seconds:
  //   • Supabase read-after-write replication lag (the row is `premium`
  //     on the primary but the next /users/me hits a replica that hasn't
  //     caught up yet).
  //   • verify-iap is still in-flight when we land here (Apple's first
  //     receipt validation can take 1-3s).
  //   • Concurrent setState batching delays the new `isPremium` value.
  //
  // Previously we bounced to /onboarding?step=paywall on the first stale
  // snapshot, OnboardingV2 polling then detected `premium` and bounced
  // back to /dashboard, and the cycle repeated → the dashboard ↔ paywall
  // flicker the user reported.
  //
  // Fix: instead of redirecting on the first non-premium snapshot, poll
  // /users/me up to MAX_REVALIDATIONS times spaced REVALIDATION_INTERVAL_MS
  // apart. Only redirect if the user is *still* non-premium after the
  // entire grace window elapses. For a genuine free user this adds a
  // ~3s delay before the paywall appears (acceptable). For a recently-
  // purchased user it eliminates the flicker entirely.
  const MAX_REVALIDATIONS = 5;
  const REVALIDATION_INTERVAL_MS = 700;
  const revalidationCountRef = useRef(0);

  useEffect(() => {
    if (userLoading || !user) return; // wait for user data to load
    // Premium, admin, beta → allowed; reset revalidation counter
    if (isPremium) {
      revalidationCountRef.current = 0;
      return;
    }
    // Still needs onboarding → send to onboarding flow
    if (user.needs_onboarding) {
      if (location.pathname !== '/onboarding') {
        navigate('/onboarding', { replace: true });
      }
      return;
    }
    // Non-premium snapshot — revalidate before bouncing.
    if (revalidationCountRef.current < MAX_REVALIDATIONS) {
      revalidationCountRef.current += 1;
      let cancelled = false;
      const timer = setTimeout(async () => {
        if (cancelled) return;
        try {
          await fetchUserData();
        } catch (_) { /* ignore — effect re-runs anyway */ }
      }, REVALIDATION_INTERVAL_MS);
      return () => { cancelled = true; clearTimeout(timer); };
    }
    // Exhausted retries — confirmed non-premium → paywall
    navigate('/onboarding?step=paywall', { replace: true });
  }, [user, isPremium, userLoading, navigate, location.pathname, fetchUserData]);

  // Garden state — lives at layout level so it persists across page switches
  const [gardenGrid, setGardenGrid] = useState(() => {
    try {
      const uid = localStorage.getItem('betterhusbandUserId') || '';
      const saved = localStorage.getItem(`bh_garden_grid_${uid}`) || localStorage.getItem('bh_garden_grid');
      if (saved) return JSON.parse(saved);
    } catch (_) { /* ignore */ }
    return createInitialGarden();
  });
  const [completionAnim, setCompletionAnim] = useState(null);
  const [forestPulse, setForestPulse] = useState(false);

  // Persist garden grid
  useEffect(() => {
    if (!userId) return;
    try {
      const json = JSON.stringify(gardenGrid);
      localStorage.setItem(`bh_garden_grid_${userId}`, json);
      localStorage.setItem('bh_garden_grid', json);
    } catch (_) { /* ignore */ }
  }, [gardenGrid, userId]);

  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'home';
    if (path === '/plan') return 'plan';
    if (path === '/stats') return 'stats';
    if (path === '/achievements') return 'achievements';
    if (path === '/notes') return 'notes';
    if (path === '/events') return 'events';
    if (path === '/history') return 'history';
    return 'home';
  };

  const { isLightMode } = useTheme();
  const isDarkForestPage = !isLightMode; // dark forest unless light mode

  // Pages that show the garden: dashboard only. The Journey screen (/plan) is
  // the journey and nothing else — the garden sub-header ate the top third of
  // it without adding anything the journey itself doesn't already show.
  const showGarden = location.pathname === '/dashboard';

  // Header variant per page
  const headerVariant = isLightMode ? 'lightForest' : 'darkForest';

  // Page slide transition logic
  const prevPathRef = useRef(location.pathname);
  const [slideClass, setSlideClass] = useState('');

  useEffect(() => {
    const prev = prevPathRef.current;
    const next = location.pathname;
    if (prev === next) return;

    const prevIdx = PAGE_ORDER[prev] ?? -1;
    const nextIdx = PAGE_ORDER[next] ?? -1;

    // Only animate between known nav pages
    if (prevIdx >= 0 && nextIdx >= 0) {
      const dir = nextIdx > prevIdx ? 'slide-from-right' : 'slide-from-left';
      setSlideClass(dir);
      const timer = setTimeout(() => setSlideClass(''), 300);
      prevPathRef.current = next;
      return () => clearTimeout(timer);
    }

    prevPathRef.current = next;
  }, [location.pathname]);

  // ── Swipe between tabs ──
  const NAV_PATHS = Object.keys(PAGE_ORDER); // ['/dashboard','/plan','/achievements','/stats']
  const touchRef = useRef({ startX: 0, startY: 0, swiping: false });

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { startX: t.clientX, startY: t.clientY, swiping: true };
  };

  const handleTouchEnd = (e) => {
    if (!touchRef.current.swiping) return;
    touchRef.current.swiping = false;

    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.startX;
    const dy = t.clientY - touchRef.current.startY;

    // Only trigger if horizontal distance > 60px and more horizontal than vertical
    if (Math.abs(dx) < 60 || Math.abs(dy) > Math.abs(dx)) return;

    const curIdx = PAGE_ORDER[location.pathname];
    if (curIdx == null) return;

    const nextIdx = dx < 0 ? curIdx + 1 : curIdx - 1; // swipe left → next, right → prev
    if (nextIdx >= 0 && nextIdx < NAV_PATHS.length) {
      navigate(NAV_PATHS[nextIdx]);
    }
  };

  return (
    <HeaderProvider>
      <div className={`${isLightMode ? 'light-forest' : 'dark dark-forest'} flex flex-col overflow-hidden`} style={{ background: 'var(--c-surface)', height: '100dvh' }}>
        {/* Sticky Header – lives inside the flex column, never scrolls */}
        <HeaderInlineWrapper variant={headerVariant} />
        
        {/* Garden sub-header – only on dashboard & plan */}
        {showGarden && (
          <GardenSubHeader gardenGrid={gardenGrid} completionAnim={completionAnim} forestPulse={forestPulse} />
        )}
        
        {/* Scrollable Content */}
        <div id="main-scroll-container" className="flex-1 overflow-y-auto overscroll-contain" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} style={{ WebkitOverflowScrolling: 'touch', borderRadius: showGarden ? '20px 20px 0 0' : 0, marginTop: showGarden ? -12 : 0, position: 'relative', zIndex: 11, background: 'var(--c-surface)', boxShadow: showGarden ? '0 -4px 12px rgba(0,0,0,0.15)' : 'none' }}>
          <style>{`
            @keyframes slideFromRight { from { transform: translateX(30%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
            @keyframes slideFromLeft  { from { transform: translateX(-30%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
            .slide-from-right { animation: slideFromRight 0.28s ease-out both; }
            .slide-from-left  { animation: slideFromLeft  0.28s ease-out both; }
          `}</style>
          <div className={`pb-20 pb-safe ${slideClass}`}>
            <Outlet context={{ gardenGrid, setGardenGrid, setCompletionAnim, setForestPulse }} />
          </div>
        </div>
        
        {/* Bottom Nav - always visible for authenticated users */}
        <BottomNav currentPage={getCurrentPage()} darkTheme={isDarkForestPage} />
      </div>
    </HeaderProvider>
  );
};

const AuthenticatedLayout = () => (
  <ThemeProvider>
    <AuthenticatedLayoutInner />
  </ThemeProvider>
);

export default AuthenticatedLayout;
